import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CardNB from '../../components/ui-nb/CardNB';
import ButtonNB from '../../components/ui-nb/ButtonNB';
import OTPVerification from '../../components/technician/OTPVerification';
import PhotoUploader from '../../components/technician/PhotoUploader';
import SignatureCanvas from '../../components/technician/SignatureCanvas';
import { useOrderQuery } from '../../hooks/useOrderQuery';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { getErrorMessage } from '../../lib/errors';
import { useToast } from '../../components/ToastNB';
import { debug, error as logError } from '../../lib/logger';

export default function DeliverEquipment() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const userId = user?.id;
  const { order, isLoading, error } = useOrderQuery(id || null);

  const [otpVerified, setOtpVerified] = useState(false);
  const [signatureBlob, setSignatureBlob] = useState<Blob | null>(null);
  const [isDelivering, setIsDelivering] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [deliveredStatusId, setDeliveredStatusId] = useState<string | null>(null);
  const { addToast } = useToast();

  // Auth protection
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // Fetch delivered status ID at mount
  useEffect(() => {
    async function fetchDeliveredStatusId() {
      const { data, error } = await supabase
        .from('status_config')
        .select('id')
        .eq('key', 'delivered')
        .single();
      
      if (error) {
        logError('Error fetching delivered status', { error: getErrorMessage(error) });
        addToast('Could not load delivery status. Please try again.', 'error');
        return;
      }
      
      if (data) {
        setDeliveredStatusId(data.id);
      }
    }
    
    fetchDeliveredStatusId();
  }, [addToast]);

  const handlePhotoUpload = (urls: string[]) => {
    setUploadedUrls(prev => [...prev, ...urls]);
  };

  const handleSignatureSave = (blob: Blob) => {
    setSignatureBlob(blob);
  };

  const isCanvasEmpty = !signatureBlob;

  const handleConfirmDelivery = async () => {
    if (!order || !otpVerified) {
      addToast('OTP verification required before delivery', 'error');
      return;
    }

    if (!signatureBlob) {
      addToast('Customer signature required', 'error');
      return;
    }

    if (!deliveredStatusId) {
      addToast('Delivery status not loaded. Please wait and try again.', 'error');
      return;
    }

    setIsDelivering(true);

    try {
      // Upload photos to delivery-photos bucket
      if (uploadedUrls.length > 0) {
        debug('Delivery photos', { urls: uploadedUrls });
      }

// Upload signature
      let signatureUrl: string | null = null
      if (signatureBlob) {
        const signaturePath = `${order.id}/signature-${Date.now()}.png`
        const { error: signatureError } = await supabase.storage
          .from('delivery-photos')
          .upload(signaturePath, signatureBlob, {
            contentType: 'image/png',
            upsert: false,
          })

        if (signatureError) {
          logError('Signature upload error', { error: getErrorMessage(signatureError) });
          addToast('Failed to upload signature', 'error');
          setIsDelivering(false)
          return
        }

        const { data: urlData } = supabase.storage.from('delivery-photos').getPublicUrl(signaturePath)
        signatureUrl = urlData.publicUrl
      }

      // Upsert delivery_verification with photo URLs (non-blocking)
      if (uploadedUrls.length > 0 || signatureUrl) {
        try {
          const collectionPhotoUrl = uploadedUrls[0] || null
          await supabase.from('delivery_verification').upsert({
            order_id: order.id,
            collection_photo_url: collectionPhotoUrl,
            signature_url: signatureUrl,
            photo_verified: true,
          }, { onConflict: 'order_id' })
        } catch (err) {
          logError('Delivery verification upsert error', { error: getErrorMessage(err) });
          // Non-blocking: continue with status transition
        }
      }

      // Call notify-status-change to transition to Delivered
      const { error: invokeError } = await supabase.functions.invoke('notify-status-change', {
        body: {
          order_id: order.id,
          new_status_id: deliveredStatusId,
          changed_by: userId,
        },
      });

      if (invokeError) {
        throw invokeError;
      }

      // Navigate back to technician home
      navigate('/en/technician');
    } catch (err) {
      logError('Delivery error', { error: getErrorMessage(err) });
      addToast(getErrorMessage(err) || 'Failed to complete delivery', 'error');
    } finally {
      setIsDelivering(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nb-cream">
        <p className="text-nb-text-muted">Redirecting to login...</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nb-cream">
        <p className="text-nb-text-muted">Loading...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nb-cream">
        <CardNB className="p-8 max-w-md w-full text-center">
          <p className="text-lg font-semibold text-black mb-2">Equipment not found</p>
          <p className="text-nb-text-muted mb-4">
            {error || 'This equipment is not available for delivery.'}
          </p>
          <ButtonNB variant="primary" size="md" onClick={() => navigate('/en/technician')}>
            Back to list
          </ButtonNB>
        </CardNB>
      </div>
    );
  }

  // Check if order is at correct status for delivery
  const isReadyForDelivery = order.status?.toLowerCase().includes('ready') ||
    order.status?.toLowerCase().includes('testing');

  return (
    <div className="min-h-screen bg-nb-cream">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-black font-nb mb-1">
            Deliver Equipment
          </h1>
          <p className="text-sm text-nb-text-muted">Confirm equipment delivery to the client</p>
        </div>

        {/* Equipment info */}
        <CardNB className="p-6 mb-6">
          <h2 className="text-lg font-semibold text-black mb-4">Equipment information</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-nb-text-muted">Folio</p>
              <p className="font-medium text-black">{order.release_code}</p>
            </div>
            <div>
              <p className="text-nb-text-muted">Client</p>
              <p className="font-medium text-black">{order.customer_name}</p>
            </div>
            <div>
              <p className="text-nb-text-muted">Device</p>
              <p className="font-medium text-black">{order.device}</p>
            </div>
            <div>
              <p className="text-nb-text-muted">Brand / Model</p>
              <p className="font-medium text-black">
                {[order.device_brand, order.device_model].filter(Boolean).join(' ') || 'N/A'}
              </p>
            </div>
            {order.imei_serial && (
              <div className="col-span-2">
                <p className="text-nb-text-muted">IMEI</p>
                <p className="font-medium font-mono text-black">{order.imei_serial}</p>
              </div>
            )}
          </div>
        </CardNB>

        {/* Not ready for delivery state */}
        {!isReadyForDelivery && !isLoading && (
          <CardNB className="p-6 mb-6 border-2 border-nb-red">
            <p className="text-center text-black font-medium">
              ⚠️ Equipment is not ready for delivery
            </p>
            <p className="text-center text-sm text-nb-text-muted mt-2">
              This equipment must be in "Ready for delivery" status before it can be delivered.
            </p>
            <ButtonNB
              variant="secondary"
              size="md"
              onClick={() => navigate('/en/technician')}
              className="mt-4 mx-auto"
            >
              Back to Orders
            </ButtonNB>
          </CardNB>
        )}

        {/* Ready for delivery flow */}
        {isReadyForDelivery && (
          <>
            {/* OTP Verification */}
            <CardNB className="p-6 mb-6">
              <h2 className="text-lg font-semibold text-black mb-4">Identity verification</h2>
              <OTPVerification
                orderId={order.id}
                method="otp_sms"
                onVerified={() => setOtpVerified(true)}
                onError={(err) => setLocalError(err)}
              />
            </CardNB>

            {/* Delivery photos */}
            <CardNB className="p-6 mb-6">
              <h2 className="text-lg font-semibold text-black mb-4">Delivery photos</h2>
              <p className="text-sm text-nb-text-muted mb-4">
                Upload photos of the equipment and ID (10MB max per photo)
              </p>
              <PhotoUploader
                orderId={order.id}
                bucket="delivery-photos"
                maxPhotos={5}
                onUploadComplete={handlePhotoUpload}
              />
            </CardNB>

            {/* Signature */}
            <CardNB className="p-6 mb-6">
              <h2 className="text-lg font-semibold text-black mb-4">Customer signature</h2>
              <p className="text-sm mb-4 text-nb-text-muted">
                Have the customer sign below to confirm delivery.
              </p>
              <SignatureCanvas onSave={handleSignatureSave} />
              {isCanvasEmpty && (
                <p className="text-xs text-nb-text-muted mt-1">
                  Signature will be captured when the customer signs
                </p>
              )}
            </CardNB>

            {/* Error display */}
            {localError && (
              <CardNB className="mb-4 p-4 border-2 border-nb-red bg-white">
                <p className="text-sm text-nb-red">{localError}</p>
              </CardNB>
            )}

            {/* Confirm delivery */}
            <ButtonNB
              variant="primary"
              size="lg"
              onClick={handleConfirmDelivery}
              loading={isDelivering}
              disabled={!otpVerified || !signatureBlob || !deliveredStatusId}
              className="w-full"
            >
              Confirm delivery
            </ButtonNB>
          </>
        )}
      </div>
    </div>
  );
}